import { sendBackgroundMessage } from '$tablet-app/src/app/chat/functions';
import { intent_labels } from '$tablet-app/src/app/chat/intent_labels';
import { useApplicationID } from '$tablet-app/src/app/chat/useAkinChat';
import Dialog from '$tablet-app/src/app/components/Dialog';
import FullScreenLoader from '$tablet-app/src/app/components/FullScreenLoader';
import { ScreenHeader } from '$tablet-app/src/app/components/ScreenHeader';
import { ScreenTitle } from '$tablet-app/src/app/components/ScreenTitle';
import { MainDrawerScreenProps } from '$tablet-app/src/app/navigation/MainDrawerNavigator';
import { useStateStore } from '$tablet-app/src/app/stores/useStateStore';
import { useCurrentUserID } from '$tablet-app/src/app/users';
import useCurrentDay from '$tablet-app/src/app/utils/useCurrentDay';
import { useMealsInfoVideo } from '$tablet-app/src/app/videos/hooks/info_video_hooks';
import { useAuthUsername } from '@akin-home/auth/react';
import { Button } from '@akin-home/buttons/native';
import { useCurrentTime } from '@akin-home/dates/react';
import { useHabitatID } from '@akin-home/habitats/react';
import { Meal, groupMealsIntoWeekPlan } from '@akin-home/meals';
import { saveMealPreferencesToStateVariables } from '@akin-home/meals/frontend';
import {
  useCreateMealFromModel,
  useDeleteMeal,
  useListMealsByHabitat,
  useQuickUpdateMeal,
} from '@akin-home/meals/react';
import { Text } from '@akin-home/text/native';
import { showInfoToast, showSuccessToast } from '@akin-home/toast/native';
import { assembleUserChatID } from '@akin-home/user';
import { useUpdateUserQuick } from '@akin-home/users/react';
import { useIsFocused } from '@react-navigation/native';
import { addWeeks, isSameDay, isSameWeek } from 'date-fns';
import React, { useEffect, useMemo, useRef } from 'react';
import { FlatList } from 'react-native';
import styled from 'styled-components/native';
import { useToggleRecipeFavourite } from '../../hooks/useToggleRecipeFavourite';
import { useMealPlanScreenState } from '../../stores/meal_plan_screen/createMealPlanScreenState';
import { MealPlanPreferences } from '../../types';
import { GeneratingMealPlanAnimation } from '../GeneratingMealPlanAnimation';
import { MealPlanDayRow } from '../MealPlanDayRow';
import MealPlanPreferencesDialog from '../MealPlanPreferencesDialog';
import { useMealPreferenceDialogState } from '../MealPlanPreferencesDialog/store/useMealPreferenceDialogState';

export const NewMealPlanScreen = (props: MainDrawerScreenProps<'Meals'>) => {
  const {
    navigation: { navigate },
  } = props;

  const user_id = useCurrentUserID();
  const username = useAuthUsername();

  const favourite_recipe_ids = useStateStore(
    (state) => state.current_user?.favourite_recipe_ids
  );

  const { data: info_video } = useMealsInfoVideo();

  const source = useMemo(
    () => (info_video?.value ? { drive_id: info_video.value } : undefined),
    [info_video]
  );

  const habitatID = useHabitatID();

  const application_id = useApplicationID();

  const current_day = useCurrentDay();
  const { data: current_hour_str } = useCurrentTime({ interval: 60 * 60 });
  const current_date = new Date(current_day);
  const deleteMeal = useDeleteMeal();
  const createMeal = useCreateMealFromModel();
  const setEaten = useQuickUpdateMeal();
  const updateUser = useUpdateUserQuick();

  const toggleRecipeFavourite = useToggleRecipeFavourite();

  const {
    week_start_date,
    generating_meal_plan,
    regenerate_meal_plan_confirmation_visible,
    setWeek,
    goToNextWeek,
    goToPreviousWeek,
    generateMealPlan,
    askToRegenerateMealPlan,
    declineRegenerateMealPlan,
    regenerateMealPlan,
  } = useMealPlanScreenState();

  const flat_list_ref = useRef<FlatList<any> | null>();

  const showPreferencesDialog = useMealPreferenceDialogState(
    (state) => state.show
  );

  const current_hour = useMemo(
    () => new Date(current_hour_str),
    [current_hour_str]
  );

  const is_focused = useIsFocused();

  const isLastWeek = isSameWeek(addWeeks(current_date, -1), week_start_date);
  const isNextWeek = isSameWeek(addWeeks(current_date, 1), week_start_date);

  const start = useMemo(() => {
    /** This is necessary, otherwise only items stored with a UTC time from {date}-00-00-00 will fetched. */
    week_start_date.setHours(0);

    return {
      between: [
        week_start_date.toISOString(),
        addWeeks(week_start_date, 1).toISOString(),
      ],
    };
  }, [week_start_date]);

  const { data, isLoading, isSuccess } = useListMealsByHabitat({
    habitatID,
    start,
  });

  const days = useMemo(() => {
    return groupMealsIntoWeekPlan({
      meals: data || [],
      week_start_date,
    });
  }, [data, week_start_date]);

  const onViewRecipe = (recipe_id: string) => {
    navigate('RecipeDetails', { recipe_id });
  };

  const onChangePreferences = async (preferences: MealPlanPreferences) => {
    showInfoToast({ text1: 'Saving Preferences' });

    await saveMealPreferencesToStateVariables({
      username: assembleUserChatID(username || '', user_id),
      application_id,
      preferences,
    });

    updateUser.mutate({
      input: { id: user_id, avoidances: preferences.avoidances },
    });

    showSuccessToast({ text1: 'Preferences Updated!' });

    askToRegenerateMealPlan();
  };

  const onGeneratePlan = () => generateMealPlan(data || [], createMeal);

  const onRegeneratePlan = async () => {
    regenerateMealPlan(data || [], createMeal, deleteMeal.mutateAsync);
  };

  const onChangeRecipe = async (meal: Meal) =>
    navigate('RecipeSelect', { meal_id: meal.id, meal_type: meal.mealType });

  const onSetMealEaten = ({ id, eaten }: Meal) =>
    setEaten.mutate({
      input: { id, eaten: !eaten },
    });

  const onPressFavourite = ({ recipes }: Meal) => {
    const recipe = recipes[0];
    recipe && toggleRecipeFavourite(recipe);
  };

  /** Update the current week when the current day changes */
  useEffect(() => {
    setWeek(new Date(current_day));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current_day]);

  /** Scroll to today when day changes, screen gains focus or data loads */
  useEffect(() => {
    if (is_focused && isSuccess) {
      const now = new Date(current_day);

      const index = days.findIndex((day) => isSameDay(now, new Date(day.date)));

      if (index >= 0) {
        flat_list_ref.current?.scrollToIndex({
          index,
          animated: true,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current_day, week_start_date, is_focused, isSuccess]);

  useEffect(
    () => {
      const should_check_meal_plan = is_focused && isSuccess && !data.length;

      /** It has loaded and is in focus, no meals currently set */
      if (should_check_meal_plan) {
        onGeneratePlan();
      }

      if (is_focused && isSuccess) {
        sendBackgroundMessage(intent_labels.open_meals);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [is_focused, isSuccess]
  );

  return (
    <>
      <ScreenHeader>
        <ScreenTitle source={source}>My meal plan</ScreenTitle>

        <Button variant="secondary" onPress={showPreferencesDialog}>
          Preferences
        </Button>
      </ScreenHeader>

      <Body>
        {generating_meal_plan ? (
          <AnimationContainer>
            <GeneratingMealPlanAnimation />
            <Text variant="h2" weight="semibold">
              Creating your meal plan!
            </Text>
          </AnimationContainer>
        ) : isLoading ? (
          <FullScreenLoader />
        ) : (
          <FlatList
            ref={(ref) => (flat_list_ref.current = ref)}
            data={days}
            getItemLayout={getItemLayout}
            ListHeaderComponent={
              // Only allow navigating to previous week, not beyond that.
              !isLastWeek ? (
                <WeekNavButton>
                  <Button onPress={goToPreviousWeek} icon="chevron-left">
                    Go To Previous Week
                  </Button>
                </WeekNavButton>
              ) : null
            }
            ListFooterComponent={
              // Only allow navigating to next week, not beyond that.
              !isNextWeek ? (
                <WeekNavButton>
                  <Button onPress={goToNextWeek} icon="chevron-right">
                    Go To Next Week
                  </Button>
                </WeekNavButton>
              ) : null
            }
            renderItem={({ item }) => (
              <MealRow
                date={item.date}
                current_time={current_hour}
                meals={item.meals}
                isToday={current_day === item.date}
                favourite_recipe_ids={favourite_recipe_ids}
                onViewRecipe={onViewRecipe}
                onSwapMeal={onChangeRecipe}
                onPressEaten={onSetMealEaten}
                onPressFavourite={onPressFavourite}
                onPressEmpty={showPreferencesDialog}
              />
            )}
          />
        )}
      </Body>

      <MealPlanPreferencesDialog onSubmitDone={onChangePreferences} />

      <Dialog visible={regenerate_meal_plan_confirmation_visible} size="mc">
        <ConfirmationModalBody>
          <Text variant="h2" weight="semibold">
            Recreate your meal plan with these preferences?
          </Text>
          <ConfirmationModalButtons>
            <Button
              variant="secondary"
              size="xlarge"
              onPress={declineRegenerateMealPlan}
            >
              No
            </Button>
            <Button onPress={onRegeneratePlan} size="xlarge">
              Yes
            </Button>
          </ConfirmationModalButtons>
        </ConfirmationModalBody>
      </Dialog>
    </>
  );
};

const getItemLayout = (_: any, index: number) => ({
  length: ROW_HEIGHT,
  offset: NAV_BUTTON_HEIGHT + ROW_HEIGHT * index,
  index,
});

const ROW_HEIGHT = 390;
const NAV_BUTTON_HEIGHT = 75;

const MealRow = styled(MealPlanDayRow)`
  height: ${ROW_HEIGHT}px;
`;

const Body = styled.View`
  flex: 1;
`;

const WeekNavButton = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: center;

  padding: 16px;

  height: ${NAV_BUTTON_HEIGHT}px;
`;

const AnimationContainer = styled.View`
  flex: 1;
  padding: 16px;
  padding-bottom: 24px;

  align-items: center;

  justify-content: flex-end;
`;

const ConfirmationModalButtons = styled.View`
  flex-direction: row;
  gap: 30px;
`;

const ConfirmationModalBody = styled.View`
  align-items: center;
  padding: 30px;
  gap: 30px;
`;
